/* eslint-disable @typescript-eslint/no-explicit-any */
import { fetchEventSource, EventSourceMessage } from '@microsoft/fetch-event-source';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { UIContext } from './uiContextManager';
import { isUnauthorizedResponse, handleUnauthorizedError } from './authErrorHandler';
import { handleTrafficDisabledResponse } from './tipFeatureFlagErrorHandler';
import { getRecentSearchesForAPI, getRecentlyViewedForAPI } from './recentSearchStorage';
import { CurrentLocation } from '../types/personalization.types';

const DEFAULT_API_TIMEOUT_MS = 15000; // 15 seconds fallback

/**
 * Custom error class for guardrail violations.
 * Thrown when the /messages API returns 403 with action: "guardrail_violation".
 */
export class GuardrailViolationError extends Error {
  constructor() {
    super('Guardrail violation');
    this.name = 'GuardrailViolationError';
  }
}

/**
 * Get the API timeout from environment variable or use default.
 */
function getApiTimeoutMs(): number {
  const envTimeout = useClientEnvVarsStore.getState().envVarsObject['TIP_API_TIMEOUT_MS'];
  return envTimeout ? Number(envTimeout) : DEFAULT_API_TIMEOUT_MS;
}

/**
 * Fetch with timeout using AbortController.
 * If the request doesn't complete within the specified timeout, it will be aborted.
 */
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs?: number): Promise<Response> {
  const timeout = timeoutMs ?? getApiTimeoutMs();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface SSEStreamCallbacks {
  onMessage: (event: EventSourceMessage) => void;
  onOpen?: () => void;
  onError?: (err: Error) => void;
  onClose?: () => void;
  onUnauthorized?: () => void;
}

export class TipSSEClient {
  private baseURL: string;
  private sessionURL: string;
  private requestId: string;
  private authStateToken: string;
  private isLocalDev: boolean;
  private memberLevel?: string;
  private currentLocation?: CurrentLocation;
  constructor(
    baseURL: string,
    sessionURL: string,
    requestId: string,
    authStateToken: string,
    isLocalDev: boolean = false,
    memberLevel?: string,
    currentLocation?: CurrentLocation
  ) {
    this.baseURL = baseURL;
    this.sessionURL = sessionURL || '';
    this.requestId = requestId || '';
    this.authStateToken = authStateToken || '';
    this.isLocalDev = isLocalDev;
    this.memberLevel = memberLevel;
    this.currentLocation = currentLocation;
  }

  updateAuthToken(newToken: string): void {
    this.authStateToken = newToken;
  }

  async createSession(): Promise<any> {
    const createSessionUrl = `${this.sessionURL}/v1/sessions`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'tipai-project': 'conversational-search',
      'tipai-team': 'web-conversational-search',
    };

    if (!this.isLocalDev) {
      headers['correlation-id'] = this.requestId;
      headers['Authorization'] = `Bearer ${this.authStateToken}`;
    }

    // Check if URL has conversational-search-smoke=true query param
    const isSmokeTest =
      typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('convoai-smoke') === 'true';
    const team = isSmokeTest ? 'conversational-search-smoke' : 'conversational-search';

    const payload: Record<string, any> = {
      bypass_intent_similarity: true,
      enabled_greeting: false,
      team,
      router_mode: true,
      llm_aggregation: false,
    };

    // Build persistent_context with member_tier, recent_searches, recently_viewed, and current_location
    const recentSearches = getRecentSearchesForAPI();
    const recentlyViewed = getRecentlyViewedForAPI();
    if (this.memberLevel || recentSearches.length > 0 || recentlyViewed.length > 0 || this.currentLocation) {
      payload['persistent_context'] = {
        ...(this.memberLevel && { member_tier: this.memberLevel }),
        ...(recentSearches.length > 0 && { recent_searches: recentSearches }),
        ...(recentlyViewed.length > 0 && { recently_viewed: recentlyViewed }),
        ...(this.currentLocation && {
          current_location: {
            city: this.currentLocation.city,
            state: this.currentLocation.state,
            country: this.currentLocation.country,
            source: 'ip' as const,
          },
        }),
      };
    }

    const createRes = await fetchWithTimeout(createSessionUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (await handleTrafficDisabledResponse(createRes)) {
      return;
    }
    if (isUnauthorizedResponse(createRes)) {
      if (this.isLocalDev) {
        throw new Error('Unauthorized');
      }
      await handleUnauthorizedError();
      return;
    }
    if (!createRes.ok) {
      throw new Error(`Failed to create session: ${createRes.status}`);
    }
    return await createRes.json();
  }

  connectStream(streamUrl: string, callbacks: SSEStreamCallbacks, lastEventId?: string): AbortController {
    // TIP may return an absolute `stream_url`. If so, do NOT prefix with baseURL.
    // Prefixing an absolute URL produces an invalid URL and can surface as ERR_HTTP2_PROTOCOL_ERROR in the browser.
    const connectStreamUrl = /^https?:\/\//i.test(streamUrl)
      ? streamUrl
      : `${this.baseURL}${streamUrl.startsWith('/') ? '' : '/'}${streamUrl}`;

    const ctrl = new AbortController();
    const headers: Record<string, string> = {
      'tipai-project': 'conversational-search',
      'tipai-team': 'web-conversational-search',
    };

    if (!this.isLocalDev) {
      headers['correlation-id'] = this.requestId;
      headers['Authorization'] = `Bearer ${this.authStateToken}`;
    }

    // Add Last-Event-Id header for reconnection (to avoid receiving duplicate events)
    if (lastEventId) {
      headers['Last-Event-ID'] = lastEventId;
    }

    const isLocalDev = this.isLocalDev;

    let handled401 = false;

    fetchEventSource(connectStreamUrl, {
      headers,
      signal: ctrl.signal,
      openWhenHidden: true,
      async onopen(response) {
        if (isUnauthorizedResponse(response)) {
          handled401 = true;
          if (isLocalDev) {
            throw new Error('Unauthorized');
          }
          if (callbacks.onUnauthorized) {
            await callbacks.onUnauthorized();
          } else {
            await handleUnauthorizedError();
          }
          return;
        }
        if (response.ok) {
          callbacks.onOpen?.();
        } else if (await handleTrafficDisabledResponse(response)) {
          return;
        } else {
          // Throw to stop retries on non-OK responses (4xx, 5xx)
          throw new Error(`SSE connection failed: ${response.status}`);
        }
      },
      onmessage(ev) {
        callbacks.onMessage(ev);
      },
      onerror(err) {
        // Don't throw if we already handled a 401 - let onUnauthorized handle the flow
        if (handled401) {
          return;
        }
        callbacks.onError?.(err instanceof Error ? err : new Error(String(err)));
        // Throw to stop infinite retries - let the caller handle reconnection logic
        throw err;
      },
      onclose() {
        callbacks.onClose?.();
      },
    });

    return ctrl;
  }
  async sendMessage(
    workflowId: string,
    message: string,
    headers: Record<string, string> = {},
    uiContext?: UIContext | null
  ): Promise<any> {
    const sendMessageUrl = `${this.sessionURL}/v1/sessions/${workflowId}/messages`;
    const payload: Record<string, any> = { prompt: message };
    if (uiContext) {
      payload['ui_context'] = uiContext;
    }
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'tipai-project': 'conversational-search',
      'tipai-team': 'web-conversational-search',
      ...headers,
    };

    if (!this.isLocalDev) {
      baseHeaders['correlation-id'] = this.requestId;
      baseHeaders['Authorization'] = `Bearer ${this.authStateToken}`;
    }
    const sendMessageRes = await fetchWithTimeout(sendMessageUrl, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(payload),
    });
    if (await handleTrafficDisabledResponse(sendMessageRes)) {
      return;
    }
    if (isUnauthorizedResponse(sendMessageRes)) {
      if (this.isLocalDev) {
        throw new Error('Unauthorized');
      }
      await handleUnauthorizedError();
      return;
    }
    if (!sendMessageRes.ok) {
      if (sendMessageRes.status === 403) {
        try {
          const errorBody = await sendMessageRes.json();
          if (errorBody?.action === 'guardrail_violation') {
            throw new GuardrailViolationError();
          }
        } catch (parseError) {
          if (parseError instanceof GuardrailViolationError) {
            throw parseError;
          }
          // JSON parse failed, fall through to generic error
        }
      }
      throw new Error(`Failed to send message: ${sendMessageRes.status}`);
    }
    return await sendMessageRes.json();
  }
  async interruptSession(sessionId: string): Promise<any> {
    try {
      const baseHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'tipai-project': 'conversational-search',
        'tipai-team': 'web-conversational-search',
      };
      const url = `${this.sessionURL}/v1/sessions/${sessionId}/commands/interrupt`;
      if (!this.isLocalDev) {
        baseHeaders['correlation-id'] = this.requestId;
        baseHeaders['Authorization'] = `Bearer ${this.authStateToken}`;
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: baseHeaders,
      });
      return await res.json();
    } catch (error) {
      console.error('Failed to interrupt TIP session:');
    }
  }
}
